import { clients } from 'lib/https';
import { User } from '/types/user-type';
import { CreateUserFormType, ProfileFormType } from '../utils/utils';
import { ConfigurationSettings } from '/models/configuration-settings';

/**
 * Fetches the account details of the currently authenticated user.
 *
 * @returns {Promise<User>} The user's account data.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const account = await getAccount();
 */

export const getAccount = async (config: ConfigurationSettings): Promise<User> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.getAccount}`;
  const res = await clients.get<{ data: User }>(url, config.blocksKey);
  return res.data;
};

/**
 * Creates a new user account with the provided form data.
 *
 * @param {CreateUserFormType} data - The form data used to create the user account.
 *
 * @returns {Promise<{ itemId: string, errors: unknown, isSuccess: boolean }>}
 * The response containing the itemId, any errors, and the success status of the creation.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await createAccount(formData);
 */

export const createAccount = (data: CreateUserFormType, config: ConfigurationSettings) => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.createAccount}`;

  return clients.post<{
    itemId: string;
    errors: unknown;
    isSuccess: boolean;
  }>(url, config.blocksKey, JSON.stringify(data));
};

/**
 * Updates the account profile of the currently authenticated user with the provided data.
 *
 * @param {ProfileFormType} data - The form data used to update the user's profile.
 *
 * @returns {Promise<{ itemId: string, errors: unknown, isSuccess: boolean }>}
 * The response containing the itemId, any errors, and the success status of the update.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await updateAccount(profileData);
 */
export const updateAccount = (data: ProfileFormType, config: ConfigurationSettings) => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.updateUserInfo}`;

  return clients.post<{
    itemId: string;
    errors: unknown;
    isSuccess: boolean;
  }>(url, config.blocksKey, JSON.stringify(data));
};

/**
 * Changes the password for the currently authenticated user.
 *
 * @param {Object} params - The password change parameters.
 * @param {string} params.newPassword - The new password to set.
 * @param {string} params.oldPassword - The current password to authenticate the change.
 *
 * @returns {Promise<any>} The response from the server after attempting to change the password.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await changePassword({ oldPassword: 'oldPass', newPassword: 'newPass' });
 */
export const changePassword = async ({
  newPassword,
  oldPassword,
  config,
}: {
  newPassword: string;
  oldPassword: string;
  config: ConfigurationSettings;
}) => {
  const payload = {
    newPassword,
    oldPassword,
    projectKey: config.blocksKey,
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.changePassword}`;

  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

/**
 * Deactivates the account of the currently authenticated user.
 * This action is irreversible and will permanently delete the user's account.
 *
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey
 * @param {string} userId - The ID of the user to deactivate
 * @returns {Promise<{ itemId: string; errors: any; isSuccess: boolean }>} A promise that resolves with the deactivation result
 * @throws {Error} If the request fails or the server returns an error
 *
 * @example
 * const result = await deactivateAccount(config, userId);
 */
export const deactivateAccount = async (
  config: ConfigurationSettings,
  userId: string,
  isDeleteForever?: boolean
): Promise<{ itemId: string; errors: any; isSuccess: boolean }> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = isDeleteForever ? `${baseUrl}${config.paths.iam.deleteUser}` : `${baseUrl}${config.paths.iam.disableUser}`;
  
  const payload = {
    userId,
    projectKey: config.blocksKey,
  };

  return clients.post<{ itemId: string; errors: any; isSuccess: boolean }>(
    url,
    config.blocksKey,
    JSON.stringify(payload)
  );
};
